import { SystemEntity } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { useAPInvoice } from '@/api/rpc/hooks/accounts-payable/useAPInvoices';
import { FileTextIcon, ArrowSquareOutIcon } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { formatCurrency } from '@erp/shared-utils';
import { SystemEntityInline } from '@/components/system-entities/inline-ui';
import paths from '@/paths';
import { T, Var, useGT } from 'gt-react';

interface APInvoicePreviewProps {
  entityType: SystemEntity; // Required by preview component registry but not used - type is always AP_INVOICE
  entityId: string;
  className?: string;
}

const STATUS_VARIANTS: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  DRAFT: 'secondary',
  MATCHED: 'default',
  ACCEPTED: 'default',
  FINALIZED: 'outline',
  VOID: 'outline',
};

export function APInvoicePreview({
  entityId,
  className,
}: APInvoicePreviewProps) {
  const gt = useGT();
  const { invoice: invoiceResponse, isLoading, error } = useAPInvoice(entityId);

  const statusLabels: Record<string, string> = {
    DRAFT: gt('Draft', { context: 'AP invoice status' }),
    MATCHED: gt('Matched', { context: 'AP invoice status' }),
    ACCEPTED: gt('Accepted', { context: 'AP invoice status' }),
    FINALIZED: gt('Finalized', { context: 'AP invoice status' }),
    VOID: gt('Void', { context: 'AP invoice status' }),
  };

  const formatCurrencyValue = (
    value: string | undefined | null,
    currency = 'USD',
  ): string => {
    if (!value) return gt('N/A', { context: 'not applicable abbreviation' });

    const numeric = Number(value);
    if (Number.isNaN(numeric))
      return gt('N/A', { context: 'not applicable abbreviation' });

    return formatCurrency(numeric, currency);
  };

  if (isLoading) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <FileTextIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            <T context="system entity preview title">AP Invoice</T>
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          <T context="loading state">Loading...</T>
        </p>
      </div>
    );
  }

  if (error || !invoiceResponse?.invoice) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <FileTextIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            <T context="system entity preview title">AP Invoice</T>
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          <T context="error loading AP invoice preview">
            Unable to load invoice details
          </T>
        </p>
      </div>
    );
  }

  const invoice = invoiceResponse.invoice;

  return (
    <div className={cn('min-w-64 max-w-sm p-3', className)}>
      <div className="flex items-center gap-2 mb-3">
        <FileTextIcon className="size-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold">{invoice.invoiceNumber}</h4>
      </div>

      <div className="space-y-2 text-xs mb-3">
        <div className="flex items-center justify-between">
          <Badge
            variant={STATUS_VARIANTS[invoice.status] || 'outline'}
            className="text-xs"
          >
            {statusLabels[invoice.status] ?? (
              <Var>{String(invoice.status)}</Var>
            )}
          </Badge>
        </div>

        <div>
          <span className="text-muted-foreground">
            <T context="AP invoice field label">Total: </T>
          </span>
          <span className="font-medium">
            {formatCurrencyValue(invoice.totalAmount, invoice.currency)}
          </span>
        </div>

        <div>
          <span className="text-muted-foreground">
            <T context="AP invoice field label">Invoice Date: </T>
          </span>
          <span className="font-medium">
            {formatDateOnlyString(invoice.invoiceDate)}
          </span>
        </div>

        {invoice.dueDate && (
          <div>
            <span className="text-muted-foreground">
              <T context="AP invoice field label">Due Date: </T>
            </span>
            <span className="font-medium">
              {formatDateOnlyString(invoice.dueDate)}
            </span>
          </div>
        )}

        {invoice.supplierId && (
          <div>
            <span className="text-muted-foreground">
              <T context="AP invoice field label">Supplier: </T>
            </span>
            <SystemEntityInline
              entityType={SystemEntity.AP_SUPPLIER}
              entityId={invoice.supplierId}
              className="text-xs"
            />
          </div>
        )}

        {invoice.paymentStatus && (
          <div>
            <span className="text-muted-foreground">
              <T context="AP invoice field label">Payment Status: </T>
            </span>
            <span className="font-medium">{invoice.paymentStatus}</span>
          </div>
        )}
      </div>

      <div className="mt-3">
        <a
          href={paths.authenticated.accountsPayable.invoice.replace(
            '$invoiceId',
            invoice.id,
          )}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center w-full px-3 py-1.5 text-xs font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
        >
          <ArrowSquareOutIcon className="size-3 mr-1.5" />
          <T context="link to full AP invoice">View Details</T>
        </a>
      </div>
    </div>
  );
}
